import Breadcrumb from "components/Navigation/Breadcrumb";
import TitleMenu from "components/Navigation/TitleMenu";

import * as ProjectStore from "store/projectSlice";
import useTranslation from "utils/hooks/useTranslation";
import { useGetLossMutation, useLazyGetResultsQuery } from "store/api";
import React, { useCallback, useEffect, useRef, useState } from "react";
import InputData from "./StepResults/InputData";
import EstimatedLoss from "./StepResults/EstimatedLoss";
import ColdResults from "./StepResults/ColdResults";
import { SvgChevronDown, SvgChevronUp, SvgCirclePlus, SvgContrast, SvgEconomique, SvgInfoOutline, SvgLikeOutline, SvgOpenEye, SvgClosedEye, SvgOrderByAsc, SvgOrderByAscActive, SvgOrderByDesc, SvgOrderByDescActive, SvgSuccess, SvgCharger } from "components/svg";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import Radio from "components/shared/inputs/radio";
import numeral from "numeral";
import { usePopup } from "providers/PopupProvider";
import { ProductPopup } from "components/popups/ProductPopup";
import NeedHelp from "components/NeedHelp";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useProject } from "utils/hooks/useProject";
import NavigationButtons from "components/NavigationButtons";
import { addNotification } from "store/componentsSlice";
import { selectProduct as selectProductAction } from "store/projectSlice";
import { LinkButton } from "components/LinkButton";
import { DimensionmentPopup } from "components/popups/DimensionmentPopup";
import { Tooltip } from "antd";
import Checkbox from "components/shared/inputs/checkbox";
import { getRecommendedItem } from "utils/helpers/project";
import { useNavigate } from "react-router";
import Button from "components/shared/button";
import { APP_URL, getAppUrl } from "utils/defines";
import NoProductHelp from "./StepResults/NoProductHelp";
import { getBoilerReliefForm, getHybridForm, getNoProductHelpForm, isNoProductHelpEnabled, type NoProductHelpAction } from "utils/helpers/noProductHelp";
import { getProjectSchemaCode } from "utils/helpers/projectSchema";

function isDiscountActive(endDateValue: unknown) {
    if (!endDateValue) return true;

    const value = String(endDateValue);
    const dateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const endDate = dateParts
        ? new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
        : new Date(value);

    if (Number.isNaN(endDate.getTime())) return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return today <= endDate;
}

const ProjectStepResults: React.FC = () => {
    const { t, language } = useTranslation();
    const { network, isEnvFr, isThermor } = useSalesNetwork();
    const dispatch = useAppDispatch();
    const { openPopup, closeLastPopup } = usePopup();
    const { computeSchemaCode, projectTraceCreate, projectTraceUpdate, updateForm, save, isHybride, previousStep } = useProject();
    const projectStore = useAppSelector((state) => state.project);
    const [luaService_getLoss] = useGetLossMutation();
    const [luaService_getResults] = useLazyGetResultsQuery();
    const [expandedProductIndex, setExpandedProductIndex] = useState<number>();
    const [enableProjectTraceCreate, setEnableProjectTraceCreate] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState<{ field?: string, order?: string }>();
    // const [checkedCompareProducts, setCheckedCompareProducts] = useState<string[]>([]);
    const [isCompareManuOpen, setIsCompareManuOpen] = useState<boolean>(false);
    const [openPromotion, setOpenPromotion] = useState();
    const [openedByClick, setOpenedByClick] = useState(false); // 👈 was opened by click
    const [isHelpCalculating, setIsHelpCalculating] = useState(false);
    const [helpResultsPendingSave, setHelpResultsPendingSave] = useState(false);
    const [arePricesVisible, setArePricesVisible] = useState(true);
    const navigate = useNavigate();

    const dimRules = network?._parameters.dimRules;
    const highlight_HP = network?._parameters.highlight_HP;
    const enableCompareProducts = network?._parameters.HP_COMPARATOR;
    const enablePriceVisibility = network?._parameters.hidePriceCustmer === true && !network?._parameters.hidePrice;
    const hidePrices = Boolean(network?._parameters.hidePrice) || (enablePriceVisibility && !arePricesVisible);

    useEffect(() => {
        setArePricesVisible(true);
    }, [network?.code, enablePriceVisibility]);

    const helpParameter = network?._parameters.Help ?? network?._parameters.help;
    const enableNoProductHelp = isNoProductHelpEnabled(helpParameter);

    const [data, setData] = useState({
        isProductInfosOpen: false,
        data: null,
        error: false,
        recommendedBy: null,
    });
    const recByRef = useRef<string | null>(null);
    const heatPumpType = projectStore.project.forms.hpChoiceStep.form.heatPumpType;
    const colorizeTxcouv = useCallback(() => {
        return (
            isEnvFr &&
            heatPumpType !== '3' &&
            projectStore.project.forms?.hpChoiceStep.form?.releveChaudiere === 'sans'
        )
    }, [projectStore, isEnvFr]);

    const colorizeTxPhybride = useCallback(() => {
        return isEnvFr && heatPumpType === '3'
    }, [projectStore, isEnvFr])

    const isAeroAndTypoIndiff = useCallback(() => {
        return heatPumpType === '1' && projectStore.project.forms.hpChoiceStep.form.typologie === '0'
    }, [projectStore])
    console.log(projectStore.project.forms.hpChoiceStep.form)

    const isErrorTxcouv = useCallback((item: any) => {
        if (colorizeTxcouv()) {
            if (item.Txcouv >= 60 && item.Txcouv < 70)
                return true;
            if (item.Txcouv >= 100 && item.Txcouv <= 140)
                return true;
        }

        if (colorizeTxPhybride()) {
            if (item.TxCouvThermo < 70 && item.TxPhybride >= 40 && item.TxPhybride < 60)
                return true;
        }
    }, [colorizeTxcouv, colorizeTxPhybride]);

    const getItemIcon = useCallback((item: any, size: string) => {

        if (colorizeTxcouv()) {
            if (item.Txcouv >= 60 && item.Txcouv < 70)
                return <icon data-svg-size={size} data-svg-color="error-color"><SvgInfoOutline /></icon>;
            if (item.Txcouv >= 70 && item.Txcouv < 100) {
                const calc = ((item.Pcalotb * 1000 + item.AppointAvailable * 1000) / projectStore.project?.deperds?.Deperd) * 100;
                if (calc >= 120) {
                    return <icon data-svg-size={size} data-svg-color="success-color"><SvgSuccess /></icon>;
                } else if (calc <= 120) {
                    return <icon data-svg-size={size} data-svg-color="success-color"><SvgCharger /></icon>;
                }
            }
            if (item.Txcouv >= 100 && item.Txcouv <= 140)
                return <icon data-svg-size={size} data-svg-color="error-color"><SvgInfoOutline /></icon>
        }
        if (colorizeTxPhybride()) {
            if (item.TxCouvThermo >= 70 && item.TxPhybride >= 40 && item.TxPhybride < 60)
                return <icon data-svg-size={size} data-svg-color="success-color"><SvgSuccess /></icon>;
            if (item.TxCouvThermo >= 70 && item.TxPhybride >= 60 && item.TxPhybride < 80)
                return <icon data-svg-size={size} data-svg-color="success-color"><SvgContrast /></icon>;
            if (item.TxCouvThermo < 70 && item.TxPhybride >= 40 && item.TxPhybride < 60)
                return <icon data-svg-size={size} data-svg-color="error-color"><SvgInfoOutline /></icon>

        }

        return <></>
    }, [colorizeTxcouv, colorizeTxPhybride])


    const showProduct = useCallback((item: any) => {
        openPopup(
            <ProductPopup
                product={item}
                closePopup={closeLastPopup}
            ></ProductPopup>)

    }, [])

    useEffect(() => { //NOSONAR
        async function fetchData() {

            const schemaCode = computeSchemaCode();
            const projectWithSchemaCode = { ...projectStore.project, schemaCode };

            let _data = projectStore.project.results ? projectStore.project.results.E1 : [];

            if (!projectStore.project.results || !projectStore.project.results.E1 || projectStore.project.results.E1.length <= 0) { // NOSONAR
                try {
                    const deperds = await luaService_getLoss({
                        env: network?.code as string,
                        project: projectWithSchemaCode,
                    }).unwrap();

                    // ProjectStore.project.deperds = await luaService.getLoss();
                    dispatch(ProjectStore.setProjectFieldValue({ field: "deperds", value: deperds }));
                    const response = await luaService_getResults({
                        env: network?.code as string,
                        project: projectWithSchemaCode
                    }).unwrap();

                    // ProjectStore.project.results = response ? response.results : null;
                    if (response) {
                        dispatch(ProjectStore.setProjectFieldValue({ field: "results", value: response.results }));
                        _data = response.results.E1;
                    } else {
                        dispatch(ProjectStore.setProjectFieldValue({ field: "results", value: null }));
                    }

                    //if (projectStore.project.results.E1 && projectStore.project.results.E1[0] // NOSONAR
                    if (response.results.E1 && response.results.E1[0] // NOSONAR
                    ) {
                        if (!recByRef.current) {
                            recByRef.current = response.results.E1[0].product.code_env;
                        }

                        // selectProduct(null, response.results.E1[0]);
                    } else {
                        // selectProduct(null, null);
                    }
                    // All cases, select null while no product selected
                    selectProduct(null, null);
                    //create project trace
                    //await projectTraceCreate();
                    setEnableProjectTraceCreate(true)
                } catch {
                    setData((prev) => ({
                        ...prev,
                        error: true,
                    }));
                }
            }

            //const _data = ProjectStore.project.results ? ProjectStore.project.results.E1 : [];

            setData((prev) => ({
                ...prev,
                data: _data
            }));

            recByRef.current = _data?.[0]?.product?.code_env;
        }

        fetchData();
    }, [])

    useEffect(() => {
        async function syncData() {
            await projectTraceCreate()
        }

        if (enableProjectTraceCreate) {
            setEnableProjectTraceCreate(false)
            syncData();
        }
    }, [enableProjectTraceCreate, projectTraceCreate])

    const selectProduct = useCallback((e: any, item: any) => {
        if (e) e.stopPropagation();
        dispatch(selectProductAction(item));
    }, [])

    useEffect(() => {
        if (!helpResultsPendingSave) return;
        setHelpResultsPendingSave(false);
        async function persistHelpResults() {
            try {
                if (projectStore.project.id) await save(projectStore.project.header);
                if (projectStore.traceId) await projectTraceUpdate();
                else await projectTraceCreate();
            } finally {
                setIsHelpCalculating(false);
            }
        }
        persistHelpResults().catch(() => { /* handled by api interceptor */ });
    }, [helpResultsPendingSave, projectStore.project.id, projectStore.project.header, projectStore.traceId, save, projectTraceUpdate, projectTraceCreate]);

    const showHelpResults = async (action: NoProductHelpAction) => {
        if (!enableNoProductHelp || isHelpCalculating) return false;
        const hpChoiceStep = projectStore.project.forms.hpChoiceStep;
        const nextForm = getNoProductHelpForm(hpChoiceStep.form, action);
        if (!nextForm) return false;
        const nextProject = {
            ...projectStore.project,
            schemaCode: getProjectSchemaCode(nextForm, projectStore.project.forms.heatModeStep.form?.zones),
            results: null,
            selectedProduct: null,
            forms: {
                ...projectStore.project.forms,
                hpChoiceStep: { ...hpChoiceStep, form: nextForm },
            },
        };
        setIsHelpCalculating(true);
        try {
            const response = await luaService_getResults({ env: network?.code as string, project: nextProject }).unwrap();
            const results = response.results?.E1 ?? [];
            updateForm("hpChoiceStep", nextProject.forms.hpChoiceStep);
            dispatch(ProjectStore.setSchemaCode(nextProject.schemaCode));
            dispatch(selectProductAction(null));
            dispatch(ProjectStore.setProjectFieldValue({ field: "results", value: response.results }));
            dispatch(ProjectStore.setSelectedCompareProductIds([]));
            dispatch(ProjectStore.setSelectedCompareProducts([]));
            dispatch(ProjectStore.saveStore());
            setData((prev) => ({ ...prev, data: results, error: false }));
            recByRef.current = results[0]?.product?.code_env;
            setHelpResultsPendingSave(true);
            return results.length > 0;
        } catch {
            setIsHelpCalculating(false);
            return false;
        }
    };

    const openBoilerRelief = () => {
        if (!enableNoProductHelp || isHelpCalculating) return;
        const hpChoiceStep = projectStore.project.forms.hpChoiceStep;
        updateForm("hpChoiceStep", {
            ...hpChoiceStep,
            form: getBoilerReliefForm(hpChoiceStep.form),
        });
        previousStep();
    };

    const openHybridChoice = () => {
        if (!enableNoProductHelp || isHelpCalculating) return;
        const hpChoiceStep = projectStore.project.forms.hpChoiceStep;
        updateForm("hpChoiceStep", {
            ...hpChoiceStep,
            form: getHybridForm(hpChoiceStep.form),
        });
        previousStep();
    };

    const noProductHelpContent = enableNoProductHelp && (
        <NoProductHelp
            form={projectStore.project.forms.hpChoiceStep.form}
            counts={projectStore.project.results?.NoSolution || [1, 2, 3]}
            isCalculating={isHelpCalculating}
            onShowResults={showHelpResults}
            onBoilerRelief={openBoilerRelief}
            onHybrid={openHybridChoice}
        />
    );

    const submit = useCallback(async () => { //NOSONAR

        if (projectStore.project.selectedProduct) {
            dispatch(ProjectStore.saveStore());
            return Promise.resolve();
        } else {
            dispatch(addNotification({
                type: "error",
                message: t('NO_SELECTED_PRODUCT_ERROR')
            }))
            return Promise.reject(false); // NOSONAR
        }


    }, [projectStore.project.selectedProduct, dispatch, t])

    const getSortField = useCallback((field: string, title: string, length: number, noTranslate: boolean = false) => {
        const isSelected = sortBy?.field === field;
        const isAsc = isSelected && sortBy?.order === "asc";
        const isDsc = isSelected && sortBy?.order === "dsc";
        return (
            <group
                data-align="center"
                data-length="autofit"
                data-justify="center"
                data-wrap="no"
                data-gap="5"
                data-min-length={length}
                data-interactive=""
                data-transparent=""
                onClick={() => {
                    if (isSelected) {
                        if (isAsc) {
                            setSortBy({ field: field, order: "dsc" })
                        } else {
                            setSortBy(undefined)
                        }
                    } else {
                        setSortBy({ field: field, order: "asc" })
                    }
                }}
            >
                <group data-length="20" data-direction="column">
                    <group
                        onClick={() => { setSortBy(!isAsc ? { field: field, order: "asc" } : {}) }}
                        data-justify="center"
                        data-align="end"
                    >
                        <icon
                            data-length="15"
                            data-height="10"
                            {...(isAsc ? { "data-color": "secondary" } : {})}
                            data-svg-size="10"
                        >{isAsc ? <SvgOrderByAscActive /> : <SvgOrderByAsc />}</icon>
                    </group>
                    <group
                        onClick={() => { setSortBy(!isDsc ? { field: field, order: "dsc" } : {}) }}
                        data-justify="center"
                        data-align="start"
                    >
                        <icon
                            data-length="15"
                            data-height="10"
                            data-outline=""

                            {...(isDsc ? { "data-color": "secondary" } : {})}
                            data-svg-size="10"
                        >{isDsc ? <SvgOrderByDescActive /> : <SvgOrderByDesc />}</icon>
                    </group>
                </group>
                <text
                    data-wrap="wrap"
                >{noTranslate ? title : t(title)}</text>
            </group>

        )
    }, [sortBy, t])

    const sortByKey = useCallback((array: any) => {
        const key: string | undefined = sortBy?.field;
        const order: string | undefined = sortBy?.order;
        if (key && array) {
            return array.map((a: any) => a).sort(function (a: any, b: any) {
                const x = key.indexOf(".") !== -1 ? a[key.split(".")[0]][key.split(".")[1]] : a[key];
                const y = key.indexOf(".") !== -1 ? b[key.split(".")[0]][key.split(".")[1]] : b[key];
                const min = order === "asc" ? -1 : 1;
                const max = order === "asc" ? 1 : -1;
                let equal_value = 0;
                if (key !== "product.price") {
                    const x_price = a["product"]["price"];
                    const y_price = b["product"]["price"];
                    equal_value = ((x_price < y_price) ? min : ((x_price > y_price) ? max : 0))
                }

                return ((x < y) ? min : ((x > y) ? max : equal_value));
            });
        } else {
            const key = isHybride() ? "TxPhybride" : "Txcouv";

            return array?.map((a: any) => a).sort(function (a: any, b: any) { return b[key] - a[key] }); // DESC
        }
    }, [sortBy])

    const recommendedProduct = getRecommendedItem(data.data, isErrorTxcouv);

    const sortByRecommended = useCallback((array: any) => {
        if (array) {
            return [...array].sort((a, b) => {
                const aRecommended = a?.Code === recommendedProduct?.Code;
                const bRecommended = b?.Code === recommendedProduct?.Code;

                const aEconomical = a.product?.code_env === recByRef.current;
                const bEconomical = b.product?.code_env === recByRef.current;

                // Most Economical goes second
                if (aEconomical && !bEconomical) return -1;
                if (!aEconomical && bEconomical) return 1;

                // Recommended goes first
                if (aRecommended && !bRecommended) return -1;
                if (!aRecommended && bRecommended) return 1;

                return 0;
            });
        }
    }, [recommendedProduct])

    const onCllickCompareProduct = useCallback(() => {
        const checkedCompareProducts = projectStore.project.selectedCompareProductIds;
        if (checkedCompareProducts?.length > 0) {
            const selectedProjects = projectStore.project.results?.E1.filter((p: any) => checkedCompareProducts.includes(p.product.code_env));
            dispatch(ProjectStore.setSelectedCompareProducts(selectedProjects));

            navigate(getAppUrl(APP_URL.CompareProducts));
        }
    }, [dispatch, navigate, projectStore.project.selectedCompareProductIds, projectStore.project.results?.E1]);

    const removeProductFromCompare = useCallback((code: string) => {
        const otherProductIds =
            projectStore.project.selectedCompareProductIds?.filter((_code: string) => _code !== code);

        dispatch(ProjectStore.setSelectedCompareProductIds(otherProductIds));
    }, [projectStore.project.selectedCompareProductIds, dispatch]);

    const onEmptySelectedCompareProducts = useCallback(() => {
        dispatch(ProjectStore.setSelectedCompareProductIds([]));
        dispatch(ProjectStore.setSelectedCompareProducts([]));
    }, []);

    const onOpenPopupDimens = useCallback((e: any) => {
        e.preventDefault();
        openPopup(<DimensionmentPopup closePopup={closeLastPopup} projectHpChoice={projectStore.project.forms?.hpChoiceStep.form}></DimensionmentPopup>)
    }, []);

    const onCheckCompareItems = useCallback((e: any, item: any) => {
        const ids = projectStore.project.selectedCompareProductIds || [];
        if (e.target.checked && ids?.length < 3) {
            dispatch(ProjectStore.setSelectedCompareProductIds([...ids, item.product.code_env]));
        } else {
            const removed = ids?.filter((code: string) => code !== item.product.code_env);
            dispatch(ProjectStore.setSelectedCompareProductIds(removed));
        }
    }, [projectStore])

    const priceVisibilityToggle = enablePriceVisibility && (
        <Button
            type="button"
            simple
            aria-label={t(arePricesVisible ? "HIDE_PRICES" : "SHOW_PRICES")}
            aria-pressed={arePricesVisible}
            title={t(arePricesVisible ? "HIDE_PRICES" : "SHOW_PRICES")}
            onClick={() => {
                setArePricesVisible((visible) => !visible);
                setOpenPromotion(undefined);
                setOpenedByClick(false);
            }}
            style={{ padding: 0, minWidth: 32, minHeight: 32, flexShrink: 0 }}
        >
            {/* <icon data-icon-size="30" data-color="main" aria-hidden="true">
                {arePricesVisible ? "visibility" : "visibility_off"}
            </icon> */}
            <icon
                data-svg-size="60"
                data-svg-color="main"
                aria-hidden="true"
            >
                {arePricesVisible ? <SvgOpenEye /> : <SvgClosedEye />}
            </icon>
        </Button >
    );

    const contentHeader = <>
        {getSortField(isHybride() ? "TxPhybride" : "Txcouv", "COVERAGE_RATE_AT", 140)}
        {getSortField("TxCouvThermo", "ANNUAL_TX_COUV", 140)}
        {getSortField("AllConso", "ESTIMATED_ANNUAL_CONSUMPTION", 160)}
        {getSortField("COPGlobalHeating", "GLOBAL_SYSTEM", 100)}
        <group data-wrap="no" data-align="center" data-gap="5" data-min-length="160" data-length="autofit">
            {getSortField("product.price", t("RECOM_PUBLIC_PRICE", { currency: network?._parameters.currency }), enablePriceVisibility ? 120 : 160, true)}
            {priceVisibilityToggle}
        </group>
    </>

    return (
        <>
            <TitleMenu title={projectStore.project.id ? projectStore.project.header.projectRef : t("MMENU_NEW_SIMULATION")} />
            <Breadcrumb
            //handleSubmit={submit}
            />

            <group data-direction="column" data-align="center" data-height="autofit">
                <group data-space-horizontal="20" data-gap="20" data-space-bottom="20" data-direction="column" data-max-length='1200' >
                    <group
                        data-width="auto"
                        data-gap="15"
                        data-text-size="15"
                        data-space-vertical="20"
                    >
                        <InputData />
                        <EstimatedLoss />
                        <group data-direction="column"
                            data-length={projectStore.project.simulationCool ? "autofit" : "200"}
                            data-justify={projectStore.project.simulationCool ? "start" : "end"}
                        >
                            {(projectStore.project.simulationCool) && <ColdResults />}
                            {(data.data as any)?.length > 0 && <group
                                data-wrap="no"
                                //data-space="20"
                                data-space-vertical="5"
                                data-max-length="1200"
                            >

                                <NavigationButtons handleSubmit={submit} hidePrev={true} hideSave={true} rightAlign={true} topSpace={0} horisontalSpace={0} />
                            </group>}
                        </group>
                    </group>
                    {dimRules && <LinkButton onClick={onOpenPopupDimens} title={t('DIMENSIONING_RULES_BUTTON')}></LinkButton>}
                </group>




                <group data-direction="column" data-background="main-color-custom-lighter" data-align="center"  >

                    {(!data.data) && <group
                        data-wrap="no"
                        data-space="20"
                        data-space-vertical="15"
                        data-gap="20"
                        data-align="center"
                        data-max-length="1200"
                        //data-adaptive="desktop-1200"
                        data-height="300"
                    >

                        <div className="preloader">
                            <div className="background" ></div>
                            <div className="loader">
                                <div></div>
                            </div>
                        </div>
                    </group>}


                    {((data.data as any)?.length === 0) &&
                        <>
                            <group
                                data-wrap="no"
                                data-space="20"
                                data-space-vertical="15"
                                data-gap="20"
                                data-align="center"
                                data-max-length="1200"
                                data-adaptive="desktop-1200"
                                data-direction="column"
                                data-min-height="300"
                            >
                                <text data-wrap="preline" data-link dangerouslySetInnerHTML={{ // NOSONAR
                                    __html: t('NO_PRODUCT').replace(/([a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,4})/gi, '<a href="mailto:$1">$1</a>'),
                                }}
                                ></text>
                                {heatPumpType == "1" ? noProductHelpContent : null}
                            </group>
                            <group
                                data-wrap="no"
                                data-space="20"
                                data-space-vertical="15"
                                data-gap="20"
                                data-align="center"
                                data-adaptive="mobile-1200"
                                data-direction="column"
                                data-min-height="300"
                            >
                                <text data-wrap="preline" data-link dangerouslySetInnerHTML={{ // NOSONAR
                                    __html: t('NO_PRODUCT').replace(/([a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,4})/gi, '<a href="mailto:$1">$1</a>'),
                                }}
                                ></text>
                                {heatPumpType == "1" ? noProductHelpContent : null}
                            </group>
                        </>}


                    {(data.data as any)?.length > 0 && <group
                        data-wrap="no"
                        data-space="20"
                        {...(isThermor ? { "data-space-bottom": "0" } : { "data-space-vertical": "15" })}
                        data-align="center"
                        data-max-length="1200"
                        data-adaptive="desktop-1200"
                    >

                        <group
                            data-wrap="no"
                            data-space-horizontal="20"
                            data-gap="20"
                            {...(isThermor ? { "data-background": "border-color", "data-space": "20" } : { "data-text-size": "12" })}
                        >
                            <group data-length="280" data-contain="" data-align="center">
                                {t("RESULT_MODEL")}
                            </group>

                            {contentHeader}
                        </group>
                    </group>
                    }

                    <group
                        data-space="20"
                        data-gap="20"
                        data-direction="column"
                        data-max-length='1200'
                    >
                        {(data.data as any)?.length > 0 && enablePriceVisibility && (
                            <group data-adaptive="mobile-1200" data-justify="right">
                                {priceVisibilityToggle}
                            </group>
                        )}
                        {
                            (sortBy || !highlight_HP ? sortByKey(data.data) : sortByRecommended(sortByKey(data.data)))?.map((item: any, index: number) => {
                                let isProductSelected = false;
                                const hasActiveDiscount = isDiscountActive(item.DiscountInfo?.end_date);
                                if (projectStore.project?.selectedProduct?.product) {
                                    isProductSelected = projectStore.project?.selectedProduct?.product.id === item.product.id
                                } else if (index === 0) {
                                    isProductSelected = true;

                                    selectProduct(null, item);
                                }

                                return <React.Fragment key={index}>
                                    <group
                                        data-direction="column"
                                        data-border="inheret"
                                        data-border-color={isProductSelected ? "main-color" : "main-color-custom-border"}
                                        {...(isThermor ? {} : { "data-radius": "5" })}
                                        data-contain=""
                                        data-background="white"
                                        data-adaptive="desktop-1200"
                                    >
                                        {item?.Code === recommendedProduct?.Code && highlight_HP && <group data-align="center" data-gap="5">
                                            <group data-position="left"
                                                data-align="center"
                                                data-width="auto"
                                                data-gap="10"
                                                data-background="main-color-custom-light"
                                                data-space="5"
                                                data-space-horizontal="10">
                                                <icon data-svg-size="16" data-svg-color="main"><SvgLikeOutline /></icon>
                                                <text data-text-size="12">{t("RECOMMENDED_SOLUTION")}</text>
                                            </group>
                                            <group data-length="autofit"></group>
                                        </group>}
                                        {!isErrorTxcouv(item) && item.product?.code_env === recByRef.current && highlight_HP && <group data-align="center" data-gap="5">
                                            <group data-position="left"
                                                data-align="center"
                                                data-width="auto" data-gap="10"
                                                data-background="main-color-custom-light"
                                                data-space="5"
                                                data-space-horizontal="10">
                                                <icon data-svg-size="16" data-svg-color="main"><SvgEconomique /></icon>
                                                <text data-text-size="12">{t("MOST_ECONOMICAL")}</text>
                                            </group>
                                            <group data-length="autofit"></group>
                                        </group>}



                                        <group data-space="10" data-gap="5" data-wrap="no">
                                            <group data-gap="10" data-wrap="no" data-length="300">
                                                <group data-gap="10">
                                                    <group data-shrink="no" data-length="120" data-max-height="120" data-radius="5">
                                                        <picture data-object-fit="contain"><img src={item.product?.photo} alt="" /></picture>
                                                        <group data-position="absolute" data-bottom="-5" data-right="-5" data-width="auto">
                                                            {getItemIcon(item, "32")}
                                                            {/* { <icon data-color="white" data-background="success-color" data-radius="30" data-length="30" data-height="30" data-icon-size="30" data-fill>check</icon> } */}
                                                        </group>
                                                    </group>
                                                    <group data-text-size="large" data-color="grey-color-mid" data-align="end">
                                                        {enableCompareProducts && <Checkbox
                                                            label={t("COMPARE")}
                                                            checkbox
                                                            checked={projectStore.project.selectedCompareProductIds?.includes(item.product.code_env)}
                                                            onChange={(e) => onCheckCompareItems(e, item)}
                                                        />}
                                                    </group>
                                                </group>
                                                <group data-direction="column" data-max-length="145">
                                                    <text data-ellipsis="" data-text-size="large" data-weight="600" data-wrap="wrap" data-color="grey-color-dark" >
                                                        {item.product.product_nom || item.Product}
                                                    </text>
                                                    {isAeroAndTypoIndiff() && <text data-text-size="large" data-color="grey-color-dark" data-wrap="wrap">
                                                        {t(`SELECT_TYPO_${item.Split_mono.toUpperCase()}`)}
                                                    </text>}
                                                    <space data-height="10"></space>
                                                    <text data-text-size="medium" data-color="grey-color-dark" data-wrap="wrap">
                                                        {t("ARTICLE_CODE")} : {item.product?.code_env}
                                                    </text>
                                                </group>
                                            </group>
                                            <group data-direction="column" >
                                                <group data-direction="row" data-wrap="no">
                                                    <group data-direction="column" data-min-length="140">
                                                        {!isHybride() &&
                                                            <>
                                                                <text data-text-size="14" data-color="grey-color-mid">
                                                                    {t("BASE")}
                                                                </text>
                                                                <text data-text-size="large" data-weight="600" data-color="grey-color-dark">
                                                                    {item.Txcouv === 0 ? t('EXTRA_ONLY') : item.Txcouv + " %"}
                                                                </text>
                                                            </>}
                                                        {colorizeTxPhybride() && <>
                                                            <space data-height="5"></space>
                                                            <text data-text-size="14" data-color="grey-color-mid" data-wrap="wrap">
                                                                {t("COVERAGE_RATE_AT_T_0_50_RE")}
                                                            </text>
                                                            <text data-text-size="large" data-weight="600" data-color="grey-color-dark">
                                                                {item.TxPhybride} %
                                                            </text></>
                                                        }
                                                    </group>
                                                    <group data-direction="column" data-color="grey-color-mid" data-min-length="140">
                                                        <text data-text-size="14">
                                                            {t("THERMO")}
                                                        </text>
                                                        <text data-text-size="large" >
                                                            {item.TxCouvThermo} %
                                                        </text>
                                                        {projectStore.project.simulationCool && colorizeTxcouv() && <>
                                                            <space data-height="5"></space>
                                                            <text data-text-size="14">
                                                                {t("GLOBAL")}
                                                            </text>
                                                            <text data-text-size="large" >
                                                                {item.ColdTxCouv} %
                                                            </text></>}
                                                    </group>
                                                    <group data-direction="column" data-color="grey-color-mid" data-min-length="160">
                                                        <text data-text-size="14">
                                                            {projectStore.project.forms.hpChoiceStep.form.typeECS === 'sans' ? t("HEATING") : t("RESULT_HEATING_DHW")}
                                                        </text>
                                                        <text data-text-size="large" >
                                                            {item.AllConso} {network?._parameters.currency}
                                                        </text>
                                                        {projectStore.project.simulationCool && colorizeTxcouv() && <>
                                                            <space data-height="5"></space>
                                                            <text data-text-size="14">
                                                                {t("RAF")}
                                                            </text>
                                                            <text data-text-size="large" >
                                                                {item.AllColdConso} {network?._parameters.currency}
                                                            </text></>}
                                                    </group>


                                                    <group data-direction="column" data-color="grey-color-mid" data-min-length="100">
                                                        <text data-text-size="14">
                                                            {t("COP")}
                                                        </text>
                                                        <text data-text-size="large" >
                                                            {item.COPGlobalHeating}
                                                        </text>
                                                        {projectStore.project.simulationCool && colorizeTxcouv() && <>
                                                            <space data-height="5"></space>
                                                            <text data-text-size="14">
                                                                {t("EER")}
                                                            </text>
                                                            <text data-text-size="large" >
                                                                {item.EERGlobal}
                                                            </text>
                                                        </>}
                                                    </group>
                                                </group>
                                                {item.product?.description_synthese && (
                                                    <group data-align="center" data-wrap="no" data-gap="10" data-space-vertical="20">
                                                        <text data-text-size="17" data-wrap="preline">
                                                            {item.product.description_synthese}
                                                        </text>
                                                    </group>
                                                )}
                                            </group>
                                            {/* </group> */}
                                            <group data-direction="column" data-color="grey-color-mid" data-gap="20" data-length="170" data-align="right">
                                                <group data-align="center" data-wrap="no" data-gap="10">

                                                    <text data-text-size="large" data-weight="600" >{hidePrices ? "-" : numeral(item.product.price).format() + " " + network?._parameters.currency}</text>
                                                    {!hidePrices && item.DiscountInfo && hasActiveDiscount && (
                                                        <Tooltip
                                                            open={openPromotion == item.Code}
                                                            onOpenChange={(nextOpen) => {
                                                                if (!nextOpen && openedByClick) return;

                                                                setOpenPromotion(prev => prev ? undefined : item.Code);
                                                                if (!nextOpen) setOpenedByClick(false);
                                                            }}
                                                            trigger={["hover", "click"]}
                                                            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                                                            placement="bottomRight"
                                                            arrow={false}
                                                            styles={{
                                                                root: {
                                                                    maxWidth: 250,
                                                                },
                                                                body: {
                                                                    background: "#fff",
                                                                    color: "#1F1F1F",
                                                                    borderRadius: 12,
                                                                    border: "1px solid #85b98e",
                                                                    boxShadow: "0 8px 24px rgba(42, 15, 20, 0.15)",
                                                                    padding: "20px",
                                                                    fontSize: 14,
                                                                    lineHeight: "20px",
                                                                },
                                                            }}
                                                            title={
                                                                <div
                                                                    style={{
                                                                        maxHeight: 90,
                                                                        overflowY: "auto",
                                                                        overflowX: "hidden",
                                                                        minHeight: 0,
                                                                        pointerEvents: "auto",
                                                                    }}
                                                                    onWheel={(e) => e.stopPropagation()}
                                                                >
                                                                    <div style={{ display: "block" }}>
                                                                        <group data-gap="10" data-color="black">
                                                                            <group data-direction="column" data-gap="10">
                                                                                <text data-text-size="12">
                                                                                    {t("FROM")}{" "}
                                                                                    {item.DiscountInfo?.start_date &&
                                                                                        new Intl.DateTimeFormat(language, {
                                                                                            dateStyle: "long",
                                                                                        }).format(
                                                                                            new Date(item.DiscountInfo.start_date)
                                                                                        )}
                                                                                </text>
                                                                            </group>

                                                                            <group data-direction="column" data-gap="10">
                                                                                <text data-text-size="12">
                                                                                    {t("TO")}{" "}
                                                                                    {item.DiscountInfo?.end_date &&
                                                                                        new Intl.DateTimeFormat(language, {
                                                                                            dateStyle: "long",
                                                                                        }).format(
                                                                                            new Date(item.DiscountInfo.end_date)
                                                                                        )}
                                                                                </text>
                                                                            </group>

                                                                            <group data-direction="column" data-gap="10">
                                                                                <text
                                                                                    style={{
                                                                                        whiteSpace: "normal",
                                                                                        wordBreak: "break-word",
                                                                                    }}
                                                                                    dangerouslySetInnerHTML={{
                                                                                        __html: item.DiscountInfo?.text,
                                                                                    }}
                                                                                />
                                                                            </group>
                                                                        </group>
                                                                    </div>
                                                                </div>
                                                            }
                                                        >

                                                            <Button data-background="success-color" onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenPromotion(prev => prev ? undefined : item.Code);
                                                                setOpenedByClick((prev) => !prev);
                                                            }}

                                                                data-color="white" data-text-size="12">{t("PROMOTION")}</Button>
                                                        </Tooltip>

                                                    )}
                                                </group>
                                                <group data-align="center" data-wrap="no" data-gap="10">
                                                    {isProductSelected ?
                                                        <div className="button large secondary primary" ><icon data-icon-size="28">check_circle</icon><text>{t("SELECTED")}</text></div>
                                                        :
                                                        <div className="button large secondary outline" onClick={(e: any) => selectProduct(e, item)} ><text>{t("SELECT")}</text></div>
                                                    }
                                                </group>

                                                <group data-align="center" data-wrap="no" data-gap="10">
                                                    <div className="button secondary simple" onClick={() => showProduct(item)}>
                                                        <icon data-fill="" data-icon-size="28" data-color="secondary">add_circle</icon>
                                                        <text>{t("PRODUCT_DETAILS")}</text>
                                                    </div>
                                                </group>
                                            </group>

                                        </group>

                                    </group>




                                    <group
                                        data-border="inheret"
                                        data-border-color={isProductSelected ? "main-color" : "main-color-custom-border"}
                                        {...(isThermor ? {} : { "data-radius": "5" })}
                                        {...(expandedProductIndex !== index ? { "data-contain": "" } : {})}
                                        data-background="white"
                                        data-adaptive="mobile-1200"
                                        // data-length="autofit"
                                        data-direction="column"
                                    //data-max-length="500"
                                    >
                                        {item?.Code === recommendedProduct?.Code && highlight_HP && <group data-align="center" data-gap="5">
                                            <group data-position="left"
                                                data-align="center"
                                                data-width="auto"
                                                data-gap="10"
                                                data-background="main-color-custom-light"
                                                data-space="5"
                                                data-space-horizontal="10">
                                                <icon data-svg-size="16" data-svg-color="main"><SvgLikeOutline /></icon>
                                                <text data-text-size="12">{t("RECOMMENDED_SOLUTION")}</text>
                                            </group>
                                            <group data-length="autofit"></group>
                                        </group>}
                                        {!isErrorTxcouv(item) && item.product?.code_env === recByRef.current && highlight_HP && <group data-align="center" data-gap="5">
                                            <group data-position="left"
                                                data-align="center"
                                                data-width="auto" data-gap="10"
                                                data-background="main-color-custom-light"
                                                data-space="5"
                                                data-space-horizontal="10">
                                                <icon data-svg-size="16" data-svg-color="main"><SvgEconomique /></icon>
                                                <text data-text-size="12">{t("MOST_ECONOMICAL")}</text>
                                            </group>
                                            <group data-length="autofit"></group>
                                        </group>}

                                        <group data-space="20" data-gap="20" data-direction="column">

                                            <group data-gap="10" data-width="auto" data-wrap="no" >
                                                <group data-width="auto" data-align="start" data-justify="center" data-wrap="no">
                                                    <group
                                                        data-length="30"
                                                    ><Radio
                                                            //name="product_id"
                                                            // label="Comparer"
                                                            checkbox
                                                            checked={isProductSelected}
                                                            onChange={(e: any) => selectProduct(e, item)}
                                                            defaultValue={projectStore.project?.selectedProduct?.product.id}
                                                            value={item.product.id}

                                                        /></group>
                                                    <group data-direction="column" data-width="auto" >
                                                        <text data-text-size="large" data-weight="600" data-color="grey-color-dark" data-wrap="wrap">
                                                            {item.Product}
                                                        </text>
                                                        <space data-height="1"></space>
                                                        {isAeroAndTypoIndiff() && <text data-text-size="medium" data-color="grey-color-dark">
                                                            {t(`SELECT_TYPO_${item.Split_mono.toUpperCase()}`)}
                                                        </text>}
                                                        <space data-height="1"></space>
                                                        <text data-text-size="15" data-color="grey-color-dark">
                                                            {t("ARTICLE_CODE")} : {item.product?.code_env}
                                                        </text>
                                                        {enableCompareProducts && <Checkbox
                                                            label={t("COMPARE")}
                                                            checkbox
                                                            checked={projectStore.project.selectedCompareProductIds?.includes(item.product.code_env)}
                                                            onChange={(e) => {
                                                                onCheckCompareItems(e, item)
                                                            }}
                                                        />}
                                                    </group>
                                                </group>
                                                <group data-position="right" data-shrink="no" data-length="110" data-max-height="110" data-radius="5">
                                                    <picture data-object-fit="contain"><img src={item.product?.photo} alt="" /></picture>
                                                </group>
                                            </group>
                                            {item.product?.description_synthese && (
                                                <group data-align="center" data-wrap="no" data-gap="10">
                                                    <text data-text-size="17" data-wrap="preline">
                                                        {item.product.description_synthese}
                                                    </text>
                                                </group>
                                            )}
                                            <separator data-horizontal=""></separator>

                                            <group data-gap="10">
                                                <group data-wrap="no" data-gap="20">
                                                    {!isHybride() ?
                                                        <>
                                                            <text data-length="autofit" data-text-size="medium" data-color="grey-color-mid" data-wrap="wrap">
                                                                {t("COVERAGE_RATE_AT")}
                                                            </text>
                                                            <text data-width="auto" data-text-size="large" data-weight="600" data-color="grey-color-dark">
                                                                {item.Txcouv === 0 ? t('EXTRA_ONLY') : item.Txcouv + " %"}
                                                            </text>
                                                        </> :
                                                        <>
                                                            <text data-length="autofit" data-text-size="medium" data-color="grey-color-mid" data-wrap="wrap">

                                                            </text>
                                                            <text data-width="auto" data-text-size="large" data-weight="600" data-color="grey-color-dark">

                                                            </text>
                                                        </>}
                                                    <group data-width="auto">
                                                        {getItemIcon(item, "24")} <text data-space-horizontal="5">*</text>
                                                    </group>

                                                </group>
                                                <group data-wrap="no" data-gap="20">
                                                    <text data-length="autofit" data-text-size="medium" data-color="grey-color-mid">
                                                        {t("SUGGESTED_PRICE", { currency: network?._parameters.currency })}
                                                    </text>
                                                    <text data-length="autofit" data-text-size="large" data-text-align="right" data-color="grey-color-dark">
                                                        {hidePrices ? "-" : numeral(item.product.price).format() + " " + network?._parameters.currency}
                                                    </text>
                                                </group>
                                                {!hidePrices && item.DiscountInfo && hasActiveDiscount && <group data-wrap="no" data-gap="20" >
                                                    <text data-length="autofit" data-text-align="right">
                                                        <text
                                                            data-space="3"
                                                            data-space-horizontal="10"
                                                            data-radius="5"
                                                            data-background="success-color"
                                                            data-color="white"
                                                            data-text-size="large"
                                                        >
                                                            {t("PROMOTION")}
                                                        </text>
                                                    </text>
                                                </group>}


                                                {
                                                    expandedProductIndex === index &&
                                                    <>
                                                        {!hidePrices && item.DiscountInfo && hasActiveDiscount && <>
                                                            <group
                                                                data-gap="10"
                                                                data-color="black"
                                                                data-length="autofit"
                                                                data-height="200"
                                                                data-border-color="success-color"
                                                                data-radius="5"
                                                                data-space="10"
                                                            >
                                                                <group data-direction="column" data-gap="10">
                                                                    <text data-text-size="12">{t("FROM")} {item.DiscountInfo?.start_date &&
                                                                        new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(new Date(item.DiscountInfo?.start_date))
                                                                    }</text>
                                                                </group>
                                                                <group data-direction="column" data-gap="10">
                                                                    <text data-text-size="12">{t("TO")} {item.DiscountInfo?.end_date &&
                                                                        new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(new Date(item.DiscountInfo?.end_date))
                                                                    }</text>

                                                                </group>
                                                                <group data-direction="column" data-gap="10">
                                                                    <text data-text-size="12">{item.DiscountInfo?.heatpump_env_code}</text>
                                                                </group>
                                                                <group data-direction="column" data-gap="10" >
                                                                    <text
                                                                        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                                                                        dangerouslySetInnerHTML={{ __html: item.DiscountInfo?.text }}
                                                                    ></text>
                                                                </group>
                                                            </group>
                                                        </>}

                                                        {colorizeTxPhybride() && <><group>

                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    {t("COVERAGE_RATE_AT")}
                                                                </text>
                                                            </group>

                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    {t("COVERAGE_RATE_AT_T_0_50_RE")}
                                                                </text>
                                                                <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                    {item.TxPhybride} %
                                                                </text>
                                                            </group>
                                                        </group></>}

                                                        <group>
                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    {t("ANNUAL_TX_COUV")}
                                                                </text>
                                                            </group>

                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    • {t("THERMO")}
                                                                </text>
                                                                <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                    {item.TxCouvThermo} %
                                                                </text>
                                                            </group>
                                                            {projectStore.project.simulationCool && colorizeTxcouv() && <>
                                                                <group data-wrap="no" data-gap="20">
                                                                    <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                        • {t("GLOBAL")}
                                                                    </text>
                                                                    <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                        {item.ColdTxCouv} %
                                                                    </text>
                                                                </group></>}
                                                        </group>


                                                        <group>
                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    {t("ESTIMATED_ANNUAL_CONSUMPTION")}
                                                                </text>
                                                            </group>

                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    • {projectStore.project.forms.hpChoiceStep.form.typeECS === 'sans' ? t("HEATING") : t("RESULT_HEATING_DHW")}
                                                                </text>
                                                                <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                    {item.AllConso} {network?._parameters.currency}
                                                                </text>
                                                            </group>
                                                            {projectStore.project.simulationCool && colorizeTxcouv() && <><group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    • {t("RAF")}
                                                                </text>
                                                                <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                    {item.AllColdConso} {network?._parameters.currency}
                                                                </text>
                                                            </group></>}
                                                        </group>


                                                        <group>
                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    {t("GLOBAL_SYSTEM")}
                                                                </text>
                                                            </group>

                                                            <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    • {t("COP")}
                                                                </text>
                                                                <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                    {item.COPGlobalHeating}
                                                                </text>
                                                            </group>
                                                            {projectStore.project.simulationCool && <group data-wrap="no" data-gap="20">
                                                                <text data-length="autofit" data-text-size="medium" data-wrap="wrap" data-color="grey-color-mid">
                                                                    • {t("EER")}
                                                                </text>
                                                                <text data-width="auto" data-text-size="large" data-color="grey-color-dark">
                                                                    {item.EERGlobal}
                                                                </text>
                                                            </group>}
                                                        </group>



                                                    </>
                                                }

                                                <space data-height="5"></space>
                                                <group data-gap="20" data-align="center">
                                                    <div className="button large secondary " data-length="forcefit" data-fit="1" onClick={() => showProduct(item)}>
                                                        <icon data-fill="" data-svg-size="16"><SvgCirclePlus /></icon>
                                                        <text data-width="auto">{t("PRODUCT_DETAILS")}</text>
                                                    </div>
                                                </group>
                                            </group>
                                            <separator data-horizontal="" data-margin="none"></separator>


                                            {expandedProductIndex !== index ?
                                                <group data-gap="20" data-align="center"
                                                    onClick={() => setExpandedProductIndex(index)}
                                                >
                                                    <div className="button secondary simple" data-length="forcefit" data-fit="1" data-gap="10">
                                                        <text data-width="auto" data-text-size="medium" data-weight="500">{t("READ_MORE")}</text>
                                                        <icon data-fill="" data-svg-size="12" data-svg-color="secondary" ><SvgChevronDown /></icon>
                                                    </div>
                                                </group>
                                                :
                                                <group data-gap="20" data-align="center"
                                                    onClick={() => setExpandedProductIndex(-1)}>
                                                    <div className="button secondary simple" data-length="forcefit" data-fit="1" data-gap="10">
                                                        <text data-width="auto" data-text-size="medium" data-weight="500">{t("READ_LESS")}</text>
                                                        <icon data-fill="" data-svg-size="12" data-svg-color="secondary" ><SvgChevronUp /></icon>
                                                    </div>
                                                </group>
                                            }
                                        </group>


                                    </group>


                                </React.Fragment>
                            })
                        }

                    </group>


                    {(data.data as any)?.length > 0 &&
                        <>
                            {(colorizeTxcouv() || 1 > 2) &&
                                <group
                                    data-space="20"
                                    data-gap="20"
                                    data-direction="column"
                                    data-max-length='1200'
                                    data-text-size="14"
                                    data-adaptive="mobile"
                                >
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgSuccess /></icon>
                                        </group>

                                        <text data-wrap="wrap">{t("RECOMMENDED_BY_ATLANTIC")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgSuccess /></icon>
                                        </group>

                                        <text data-wrap="wrap">{t("RECOMMENDED_WITH_AP")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="error-color"
                                                data-svg-size="20"
                                            ><SvgInfoOutline /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("UNOPTIMIZED_SOLUTION")}</text>
                                    </group>

                                    <group data-gap="20" dangerouslySetInnerHTML={{ __html: t("LEGEND_NOTE") }}></group>
                                    {/* <group data-gap="20" dangerouslySetInnerHTML={{ __html: t("COMMENT_AERO") }}></group> */}
                                </group>}

                            {(colorizeTxPhybride() || 2 > 11) &&
                                <group
                                    data-space="20"
                                    data-gap="20"
                                    data-direction="column"
                                    data-max-length='1200'
                                    data-text-size="14"
                                    data-adaptive="mobile"
                                >
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgSuccess /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("OTHER_SOLUTION_HYBRID")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgContrast /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("RECOMMENDED_BY_ATLANTIC_HYBRID")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="error-color"
                                                data-svg-size="20"
                                            ><SvgInfoOutline /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("UNOPTIMIZED_SOLUTION_HYBRID")}</text>
                                    </group>
                                </group>}
                        </>
                    }

                    {(data.data as any)?.length > 0 &&
                        <>
                            {(colorizeTxcouv() || 1 > 2) &&
                                <group
                                    data-space="20"
                                    data-gap="20"
                                    data-direction="column"
                                    data-max-length='1200'
                                    data-text-size="14"
                                    data-adaptive="desktop"
                                >
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgSuccess /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("RECOMMENDED_BY_ATLANTIC")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-length="30">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgCharger /></icon>
                                        </group>

                                        <text data-wrap="wrap">{t("RECOMMENDED_WITH_AP")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-width="auto">
                                            <icon
                                                data-svg-color="error-color"
                                                data-svg-size="20"
                                            ><SvgInfoOutline /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("UNOPTIMIZED_SOLUTION")}</text>
                                    </group>

                                    <group data-gap="20" dangerouslySetInnerHTML={{ __html: t("LEGEND_NOTE") }}></group>
                                    {/* <group data-gap="20" dangerouslySetInnerHTML={{ __html: t("COMMENT_AERO") }}></group> */}
                                </group>}

                            {(colorizeTxPhybride() || 2 > 11) &&
                                <group
                                    data-space="20"
                                    data-gap="20"
                                    data-direction="column"
                                    data-max-length='1200'
                                    data-text-size="14"
                                    data-adaptive="desktop"
                                >
                                    <group data-gap="20" data-wrap="no">
                                        <group data-width="auto">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgSuccess /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("OTHER_SOLUTION_HYBRID")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-width="auto">
                                            <icon
                                                data-svg-color="success-color"
                                                data-svg-size="20"
                                            ><SvgContrast /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("RECOMMENDED_BY_ATLANTIC_HYBRID")}</text>
                                    </group>
                                    <group data-gap="20" data-wrap="no">
                                        <group data-width="auto">
                                            <icon
                                                data-svg-color="error-color"
                                                data-svg-size="20"
                                            ><SvgInfoOutline /></icon>
                                        </group>
                                        <text data-wrap="wrap">{t("UNOPTIMIZED_SOLUTION_HYBRID")}</text>
                                    </group>
                                </group>}
                        </>
                    }

                </group>

                <NavigationButtons handleSubmit={submit} />
            </group >
            {enableCompareProducts && projectStore.project.selectedCompareProductIds?.length > 0 &&
                <group
                    data-sticky-bottom="60"
                    data-sticky-level="90"
                >
                    <group
                        data-justify="center"
                        data-align="center"
                        data-gap="3"
                        data-background='secondary'
                        // data-wrap="no"
                        data-adaptive='mobile'
                        data-space="10"
                        data-space-horizontal="20"
                    >

                        <Button secondary
                            accent-light
                            outline
                            mini
                            data-min-length="120"
                            text={t("COMPARE")}
                            onClick={onCllickCompareProduct}></Button>
                        {" "}

                        <Button
                            mini
                            special
                            data-border="inset"
                            data-min-length="80"
                            onClick={onEmptySelectedCompareProducts}>
                            {t("EMPTY")}
                        </Button>
                        <group
                            data-wrap="no"
                            data-align="center"
                            data-justify='center'
                            data-text-size="12"
                        >
                            <Button
                                style={{ color: "white" }}
                                secondary simple
                                material={false}
                                onClick={() => setIsCompareManuOpen((prev) => !prev)}
                                data-text-size="12"
                                icon={isCompareManuOpen ? "expand_less" : "expand_more"}
                                textFirst={true}
                                text={t("SEE_COMPARATEUR", { nbPAC: projectStore.project.selectedCompareProductIds?.length })}
                            >
                            </Button>
                        </group>
                    </group>
                    {isCompareManuOpen &&
                        <group data-min-height="100" data-duration=".5" data-background="white" data-direction="row" data-gap="10" data-align="center" data-space="10" data-adaptive='mobile'>
                            {projectStore.project.selectedCompareProductIds?.map((code: string) => {
                                const item = projectStore.project.results?.E1.find((p: any) => p.product.code_env === code);
                                return <group data-border="inset" data-border-color="main-color-lighter" data-radius="10"
                                    data-direction="row" data-align="center" data-width="auto" data-gap="10" data-space="5" data-max-length="200" data-length="autofit"
                                    data-min-height="100">
                                    <group data-direction="column" data-length="autofit" data-gap="5">
                                        <group data-length="50">
                                            <picture>
                                                <img src={item?.product.photo} alt="" data-radius="3" />
                                            </picture>
                                        </group>
                                        <group data-direction="column" data-justify="center">
                                            <text data-text-size="10" data-wrap="wrap" data-color="grey-color-dark">
                                                {item?.product.product_nom}
                                            </text>
                                            {isAeroAndTypoIndiff() && <text data-text-size="10" data-color="grey-color-dark" data-wrap="wrap">
                                                {t(`SELECT_TYPO_${item?.Split_mono?.toUpperCase()}`)}
                                            </text>}
                                        </group>
                                    </group>
                                    <group data-width="auto">
                                        <a
                                            href="#"
                                            data-type="group"
                                            data-width="auto"
                                            data-gap="5"
                                            data-interactive="highlight"
                                            onClick={() => removeProductFromCompare(code)}
                                        >
                                            <icon data-color="secondary" data-icon-size="24">
                                                cancel
                                            </icon>
                                        </a>
                                    </group>
                                </group>
                            })}

                        </group>
                    }
                </group>
            }
            <NeedHelp />
            {/* <view > */}
            {enableCompareProducts && projectStore.project.selectedCompareProductIds?.length > 0 && <>
                <group data-sticky="bottom"
                    data-sticky-level="80">
                    <group
                        data-justify="center"
                        data-align="center"
                        data-gap="5"
                        data-index="103"
                        data-background='secondary'
                        data-wrap="no"
                        data-adaptive='desktop'
                        data-space="10"
                    >
                        <Button
                            secondary
                            // accent-light
                            outline
                            large
                            onClick={onCllickCompareProduct}>{t("COMPARE")}</Button>
                        {" "}
                        <Button
                            special
                            large
                            data-border="inset"
                            onClick={onEmptySelectedCompareProducts}>{t("EMPTY")}</Button>
                        <group
                            data-wrap="no"
                            data-space="10"
                            data-space-vertical="15"
                            data-gap="20"
                            data-align="center"
                            data-justify='center'
                            data-text-size="large"
                        >
                            <Button
                                style={{ color: "white" }}
                                secondary
                                simple
                                material={false}
                                onClick={() => setIsCompareManuOpen((prev) => !prev)}
                                data-text-size="large"
                                icon={isCompareManuOpen ? "expand_less" : "expand_more"}
                                textFirst={true}
                                text={t("SEE_COMPARATEUR", { nbPAC: projectStore.project.selectedCompareProductIds?.length })}
                            >
                            </Button>
                        </group>
                    </group>
                    {isCompareManuOpen &&
                        <group data-height="100" data-duration=".5" data-background="white" data-direction="row" data-gap="10" data-align="center" data-space="10" data-adaptive='desktop'>
                            {projectStore.project.selectedCompareProductIds?.map((code: string) => {
                                const item = projectStore.project.results?.E1.find((p: any) => p.product.code_env === code);

                                return <group data-height="80" data-border="inset" data-border-color="main-color-lighter"
                                    data-radius="10" data-direction="row" data-align="center" data-width="auto">
                                    <group data-length="100">
                                        <picture><img data-height="80" data-length="90" src={item?.product.photo} alt="" data-radius="10" /></picture>

                                    </group>
                                    <group data-length="150" data-direction="column" data-gap="5" data-justify="center">
                                        <text data-text-size="small" data-wrap="wrap" data-color="grey-color-dark" >
                                            {item?.product.product_nom}
                                        </text>
                                        {isAeroAndTypoIndiff() && <text data-text-size="small" data-color="grey-color-dark" data-wrap="wrap">
                                            {t(`SELECT_TYPO_${item?.Split_mono?.toUpperCase()}`)}
                                        </text>}

                                    </group>
                                    <group data-length="50">
                                        <a
                                            href="#"
                                            data-type="group"
                                            data-width="auto"
                                            data-gap="5"
                                            data-interactive="highlight"
                                            onClick={() => removeProductFromCompare(code)}
                                        >
                                            <icon data-color="secondary" data-icon-size="24">
                                                cancel
                                            </icon>
                                        </a>
                                    </group>
                                </group>
                            })}

                        </group>
                    }
                </group>

            </>
            }


            {/* </view> */}
        </>
    )
}

export default ProjectStepResults;